import { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUp, ChevronsDown } from 'lucide-react';
import type { ColumbusPlannerTermModel } from '@/api/generated/schemas';
import type { TermPlan, TermCalculation, GroupCost, PaxCost } from '../../columbusTypes';
import { getColorHex, getColorI18nKey } from '../../columbusColors';
import { emptyPlan } from '../../columbusPlans';
import { PlanEdit } from '../../pricing/components/PlanEdit';
import { TermRow } from './TermRow';
import { TermDetail } from './TermDetail';

interface Props {
  colorCbId: number | null;
  terms: ColumbusPlannerTermModel[];
  plans: Record<string, TermPlan>;
  calculations: TermCalculation[];
  groupCostNames: Pick<GroupCost, 'name'>[];
  paxCostNames: Pick<PaxCost, 'name'>[];
  maxTariffsCount: number;
  productId: number;
  destinationSeasonId: number;
  termsSerialId: number;
  initialExpandedTermId?: number;

  onUpdateTariff: (
    planKey: string,
    index: number,
    field: 'pax' | 'retailPrice',
    value: number | null
  ) => void;
  onAddTariff: (planKey: string) => void;
  onRemoveTariff: (planKey: string, index: number) => void;
  onUpdateGroupCostName: (index: number, name: string) => void;
  onUpdateGroupCostPrice: (planKey: string, index: number, price: number | null) => void;
  onAddGroupCost: () => void;
  onRemoveGroupCost: (index: number) => void;
  onUpdatePaxCostName: (index: number, name: string) => void;
  onUpdatePaxCostPrice: (planKey: string, index: number, price: number | null) => void;
  onAddPaxCost: () => void;
  onRemovePaxCost: (index: number) => void;
  onUpdateTicketPrice: (
    planKey: string,
    field: 'depTicketPrice' | 'retTicketPrice',
    value: number | null
  ) => void;
}

export function TermsGroup({
  colorCbId,
  terms,
  plans,
  calculations,
  groupCostNames,
  paxCostNames,
  maxTariffsCount,
  productId,
  destinationSeasonId,
  termsSerialId,
  initialExpandedTermId,
  onUpdateTariff,
  onAddTariff,
  onRemoveTariff,
  onUpdateGroupCostName,
  onUpdateGroupCostPrice,
  onAddGroupCost,
  onRemoveGroupCost,
  onUpdatePaxCostName,
  onUpdatePaxCostPrice,
  onAddPaxCost,
  onRemovePaxCost,
  onUpdateTicketPrice,
}: Props) {
  const { t } = useTranslation('columbus-planner');
  const [isGroupExpanded, setIsGroupExpanded] = useState(true);
  const [areTermsExpanded, setAreTermsExpanded] = useState(true);
  const [expandedTermId, setExpandedTermId] = useState<number | null>(() =>
    initialExpandedTermId != null && terms.some((t) => t.id === initialExpandedTermId)
      ? initialExpandedTermId
      : null
  );

  // Syncuj pouze když se změní externí `initialExpandedTermId` (např. deep-link).
  // Záměrně neslouží na změny `terms` — termíny se přenačítají po nákupu letenky
  // a uživatelův expand stav by se jinak resetoval.
  useEffect(() => {
    if (initialExpandedTermId != null) {
      setExpandedTermId(initialExpandedTermId);
    }
  }, [initialExpandedTermId]);

  const colorHex = getColorHex(colorCbId);
  const colorI18nKey = getColorI18nKey(colorCbId);
  const groupPlanKey = colorCbId != null ? `color:${colorCbId}` : 'default';

  const groupPlan =
    plans[groupPlanKey] ??
    emptyPlan(groupCostNames as GroupCost[], paxCostNames as PaxCost[], maxTariffsCount);

  const expandedTerm =
    expandedTermId != null ? (terms.find((t) => t.id === expandedTermId) ?? null) : null;

  function getEffectivePlan(term: ColumbusPlannerTermModel): TermPlan {
    const termKey = `term:${term.id}`;
    return (
      plans[termKey] ??
      plans[groupPlanKey] ??
      plans['default'] ??
      emptyPlan(groupCostNames as GroupCost[], paxCostNames as PaxCost[], maxTariffsCount)
    );
  }

  const borderStyle: React.CSSProperties = colorHex
    ? { borderColor: colorHex }
    : { borderColor: 'var(--phx-border-3)' };

  return (
    <div className="mt-5">
      <div
        className="flex w-fit min-w-full gap-0 border border-t-4 border-solid p-2"
        style={borderStyle}
      >
        {/* Left panel: group settings + term rows */}
        <div className="shrink-0">
          {/* Group header */}
          <div className="mb-0.5 flex items-center gap-1.5">
            <button
              type="button"
              className="text-phx-text-2 hover:bg-phx-surface-3 hover:text-phx-text rounded p-0.5 transition-colors"
              onClick={() => setIsGroupExpanded((e) => !e)}
              title={
                isGroupExpanded
                  ? t('termsGroup.hideGroupSettings')
                  : t('termsGroup.showGroupSettings')
              }
            >
              {isGroupExpanded ? (
                <ChevronsUp className="h-3 w-3" />
              ) : (
                <ChevronsDown className="h-3 w-3" />
              )}
            </button>
            <span className="text-xs font-semibold" style={colorHex ? { color: colorHex } : {}}>
              {colorI18nKey != null ? t(colorI18nKey) : t('termsGroup.noColor')}
            </span>
          </div>

          {/* Group plan edit */}
          {isGroupExpanded && (
            <div className="mb-1">
              <PlanEdit
                planKey={groupPlanKey}
                plan={groupPlan}
                groupCostNames={groupCostNames}
                paxCostNames={paxCostNames}
                maxTariffsCount={maxTariffsCount}
                onUpdateTariff={(i, f, v) => onUpdateTariff(groupPlanKey, i, f, v)}
                onAddTariff={() => onAddTariff(groupPlanKey)}
                onRemoveTariff={(i) => onRemoveTariff(groupPlanKey, i)}
                onUpdateGroupCostName={onUpdateGroupCostName}
                onUpdateGroupCostPrice={(i, v) => onUpdateGroupCostPrice(groupPlanKey, i, v)}
                onAddGroupCost={onAddGroupCost}
                onRemoveGroupCost={onRemoveGroupCost}
                onUpdatePaxCostName={onUpdatePaxCostName}
                onUpdatePaxCostPrice={(i, v) => onUpdatePaxCostPrice(groupPlanKey, i, v)}
                onAddPaxCost={onAddPaxCost}
                onRemovePaxCost={onRemovePaxCost}
                onUpdateTicketPrice={(f, v) => onUpdateTicketPrice(groupPlanKey, f, v)}
              />
            </div>
          )}

          {/* Terms table */}
          <div className="mt-2">
            {/* Column headers */}
            <div className="border-t-phx-warning gap-phx-4 flex items-center border-t-2 border-solid px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
              <strong className="flex w-28 flex-initial items-center gap-1">
                <button
                  type="button"
                  className="text-phx-text-2 hover:bg-phx-surface-3 hover:text-phx-text rounded p-0.5 transition-colors"
                  onClick={() => setAreTermsExpanded((e) => !e)}
                >
                  {areTermsExpanded ? (
                    <ChevronsUp className="h-3 w-3" />
                  ) : (
                    <ChevronsDown className="h-3 w-3" />
                  )}
                </button>
                {t('termsGroup.colTerms')}
              </strong>
              {areTermsExpanded && (
                <Fragment>
                  <div className="w-5 flex-auto text-right">{t('termsGroup.colBreak')}</div>
                  <div className="w-14 flex-auto text-right">{t('termsGroup.colPax')}</div>
                  <div className="w-16 flex-auto text-right">{t('termsGroup.colProfit')}</div>
                  <div className="w-16 flex-auto text-right">{t('termsGroup.colMaxProfit')}</div>
                  <div className="w-16 flex-auto text-right">{t('termsGroup.colGroupCost')}</div>
                  <div className="w-24 flex-auto">{t('termsGroup.colPerPax')}</div>
                  <div className="w-4 flex-initial" />
                  <div className="w-6 flex-initial" />
                </Fragment>
              )}
            </div>

            {/* Term rows */}
            {areTermsExpanded &&
              terms.map((term, idx) => (
                <TermRow
                  key={term.id}
                  term={term}
                  calculation={calculations.find((c) => c.termId === term.id)}
                  isExpanded={expandedTermId === term.id}
                  hasCustomPlan={!!plans[`term:${term.id}`]}
                  rowIdx={idx}
                  onToggleExpand={() =>
                    setExpandedTermId((prev) => (prev === term.id ? null : term.id))
                  }
                />
              ))}
          </div>
        </div>

        {/* Right panel: term detail (horizontal) */}
        {expandedTerm != null && (
          <div className="my-1 ml-5 shrink-0">
            <TermDetail
              key={expandedTerm.id}
              term={expandedTerm}
              plan={plans[`term:${expandedTerm.id}`]}
              effectivePlan={getEffectivePlan(expandedTerm)}
              hasCustomPlan={!!plans[`term:${expandedTerm.id}`]}
              groupCostNames={groupCostNames}
              paxCostNames={paxCostNames}
              maxTariffsCount={maxTariffsCount}
              planKey={`term:${expandedTerm.id}`}
              productId={productId}
              destinationSeasonId={destinationSeasonId}
              termsSerialId={termsSerialId}
              onUpdateTariff={onUpdateTariff}
              onAddTariff={onAddTariff}
              onRemoveTariff={onRemoveTariff}
              onUpdateGroupCostName={onUpdateGroupCostName}
              onUpdateGroupCostPrice={onUpdateGroupCostPrice}
              onAddGroupCost={onAddGroupCost}
              onRemoveGroupCost={onRemoveGroupCost}
              onUpdatePaxCostName={onUpdatePaxCostName}
              onUpdatePaxCostPrice={onUpdatePaxCostPrice}
              onAddPaxCost={onAddPaxCost}
              onRemovePaxCost={onRemovePaxCost}
              onUpdateTicketPrice={onUpdateTicketPrice}
            />
          </div>
        )}
      </div>
    </div>
  );
}
